/**
 * UpcomingAppointmentNotice.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Aviso do drawer de novo agendamento: o paciente já tem atendimento marcado
 * do dia escolhido em diante. Evita criar um segundo agendamento e deixar o
 * antigo esquecido, ocupando horário.
 *
 *  - Mesmo profissional: tom de atenção e a escolha é obrigatória
 *    (reagendar um deles ou manter os dois).
 *  - Outro profissional: tom informativo; criar um novo continua sendo o padrão.
 *
 * @module UpcomingAppointmentNotice
 */

import React from 'react';
import { procedureLabels } from '../../utils/appointmentUtils';
import styles from './UpcomingAppointmentNotice.module.css';

const WEEKDAYS = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'];
const MAX_VISIBLE = 3;

/** "2026-10-20 14:00:00" → "ter, 20/10 às 14:00" */
export function formatUpcomingDate(raw) {
  const [datePart = '', timePart = ''] = String(raw || '').split(' ');
  const [y, m, d] = datePart.split('-').map(Number);
  if (!y || !m || !d) return '';
  const weekday = WEEKDAYS[new Date(y, m - 1, d).getDay()];
  return `${weekday}, ${String(d).padStart(2, '0')}/${String(m).padStart(2, '0')} às ${timePart.slice(0, 5)}`;
}

/** Agendamentos com o profissional escolhido no drawer */
export function sameProfessionalUpcoming(upcoming, resourceId) {
  if (!resourceId) return [];
  return upcoming.filter(a => String(a.resource) === String(resourceId));
}

const CalendarIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="3" y="4" width="18" height="18" rx="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

/** "2026-10-20 14:00:00" → { weekday: 'ter', day: '20/10', time: '14:00' } */
function splitUpcomingDate(raw) {
  const [datePart = '', timePart = ''] = String(raw || '').split(' ');
  const [y, m, d] = datePart.split('-').map(Number);
  if (!y || !m || !d) return { weekday: '', day: '', time: '' };
  return {
    weekday: WEEKDAYS[new Date(y, m - 1, d).getDay()],
    day: `${String(d).padStart(2, '0')}/${String(m).padStart(2, '0')}`,
    time: timePart.slice(0, 5),
  };
}

/**
 * Profissional só aparece quando não é o escolhido no drawer (seria repetição).
 * Procedimentos: todas as linhas ("Axila + Virilha"), como na agenda; sem o
 * cadastro à mão, o nome do principal que o endpoint já manda.
 */
function describe(apt, resourceId, procedures) {
  const showResource = !resourceId || String(apt.resource) !== String(resourceId);
  const procedureText = procedureLabels(apt, procedures) || apt.procedureName;
  return [showResource && apt.resourceName, procedureText].filter(Boolean).join(' · ');
}

/**
 * @param {Object} props
 * @param {string} props.patientName
 * @param {Array} props.upcoming - próximos agendamentos (patient_upcoming.php)
 * @param {string} props.resourceId - profissional escolhido no drawer
 * @param {Array} [props.procedures] - cadastro de procedimentos (CompanyConfig), para o nome das linhas
 * @param {{type: 'keep'}|{type: 'reschedule', appointment: Object}|null} props.choice
 * @param {Function} props.onChoose - (choice|null) => void
 * @param {string} [props.error]
 */
export default function UpcomingAppointmentNotice({
  patientName,
  upcoming,
  resourceId,
  procedures = [],
  choice,
  onChoose,
  error,
}) {
  if (!upcoming || upcoming.length === 0) return null;

  const firstName = (patientName || '').split(' ')[0] || 'O paciente';
  const sameProfessional = sameProfessionalUpcoming(upcoming, resourceId);
  const isAttention = sameProfessional.length > 0;

  // Já decidido: resumo compacto com a opção de voltar atrás
  if (choice?.type === 'reschedule') {
    return (
      <div className={`${styles.notice} ${styles.noticeChosen}`}>
        <span className={styles.summaryText}>
          O atendimento de <strong>{formatUpcomingDate(choice.appointment.date)}</strong> será
          reagendado para este horário.
          <span className={styles.summaryHint}>
            Agenda, procedimentos e sala vieram dele; ajuste se precisar.
          </span>
          {choice.appointment.paid && (
            <span className={styles.summaryHint}>
              O pagamento dele passa para o novo agendamento.
            </span>
          )}
        </span>
        <button type="button" className={styles.linkBtn} onClick={() => onChoose(null)}>
          Desfazer
        </button>
      </div>
    );
  }

  if (choice?.type === 'keep' && isAttention) {
    return (
      <div className={`${styles.notice} ${styles.noticeChosen}`}>
        <span className={styles.summaryText}>Os dois agendamentos serão mantidos.</span>
        <button type="button" className={styles.linkBtn} onClick={() => onChoose(null)}>
          Alterar
        </button>
      </div>
    );
  }

  // Os do mesmo profissional primeiro: são os candidatos naturais a reagendar
  const ordered = isAttention
    ? [...sameProfessional, ...upcoming.filter(a => !sameProfessional.includes(a))]
    : upcoming;
  const visible = ordered.slice(0, MAX_VISIBLE);
  const hidden = ordered.length - visible.length;

  const title = isAttention
    ? `${firstName} já tem horário nesta agenda`
    : resourceId
      ? `${firstName} também tem horário em outra agenda`
      : `${firstName} já tem horário marcado`;
  const subtitle = isAttention
    ? 'Reagende o horário marcado ou mantenha os dois.'
    : 'Se for o caso, você pode reagendar em vez de criar outro.';

  return (
    <div
      className={`${styles.notice} ${isAttention ? styles.noticeAttention : styles.noticeInfo}`}
      role={isAttention ? 'alert' : 'status'}
    >
      <div className={styles.header}>
        <span className={styles.icon}><CalendarIcon /></span>
        <div className={styles.headerText}>
          <span className={styles.title}>{title}</span>
          <span className={styles.subtitle}>{subtitle}</span>
        </div>
      </div>

      <ul className={styles.list}>
        {visible.map(apt => {
          const { weekday, day, time } = splitUpcomingDate(apt.date);
          const meta = describe(apt, resourceId, procedures);
          return (
            <li key={apt.id} className={styles.item}>
              <span className={styles.dateTile}>
                <span className={styles.dateWeekday}>{weekday}</span>
                <span className={styles.dateDay}>{day}</span>
              </span>
              <div className={styles.itemInfo}>
                <span className={styles.itemTime}>
                  {time}
                  {apt.paid && <span className={styles.paidTag}>Pago</span>}
                </span>
                {meta && <span className={styles.itemMeta}>{meta}</span>}
              </div>
              <button
                type="button"
                className={styles.rescheduleBtn}
                onClick={() => onChoose({ type: 'reschedule', appointment: apt })}
              >
                Reagendar
              </button>
            </li>
          );
        })}
      </ul>

      {hidden > 0 && (
        <span className={styles.more}>
          e mais {hidden} {hidden === 1 ? 'agendamento' : 'agendamentos'}
        </span>
      )}

      {isAttention && (
        <div className={styles.footer}>
          <button type="button" className={styles.keepBtn} onClick={() => onChoose({ type: 'keep' })}>
            Manter os dois
          </button>
        </div>
      )}

      {error && <span className={styles.error}>{error}</span>}
    </div>
  );
}
